/**
 * POC展示配置页面
 * 路径：/system/poc-display-settings
 *
 * 功能：控制官网三个POC模块的展示与隐藏，开关切换自动保存
 *
 * @author page
 * @date 2026-08-04
 */

import React, {useEffect, useState} from 'react';
import {Card, message, Switch, Typography} from 'antd';
import {SettingOutlined} from '@ant-design/icons';
import {PocDisplayConfig, systemConfigService} from '../../services/systemConfigService';

const {Text, Title} = Typography;

/**
 * POC开关配置项
 */
interface PocSwitchItemProps {
    label: string;
    description: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    saving?: boolean;
}

const PocSwitchItem: React.FC<PocSwitchItemProps> = ({
                                                         label,
                                                         description,
                                                         checked,
                                                         onChange,
                                                         disabled = false,
                                                         saving = false
                                                     }) => (
    <Card style={{marginBottom: '16px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
            <div style={{flex: 1}}>
                <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                    <SettingOutlined style={{marginRight: '8px', color: 'var(--pt-blue)'}}/>
                    <Title level={5} style={{margin: 0}}>{label}</Title>
                </div>
                <Text type="secondary" style={{display: 'block', marginBottom: '8px'}}>
                    {description}
                </Text>
                <Text type="secondary" style={{fontSize: '12px'}}>
                    启用后将在官网导航栏显示该POC入口，用户可正常访问 POC 详情页面
                </Text>
            </div>
            <div style={{marginLeft: '24px'}}>
                <Switch checked={checked} onChange={onChange} disabled={disabled || saving} loading={saving}/>
                <div style={{textAlign: 'center', marginTop: '4px'}}>
                    <Text type={checked ? 'success' : 'secondary'} style={{fontSize: '12px'}}>
                        {checked ? '已启用' : '已禁用'}
                    </Text>
                </div>
            </div>
        </div>
    </Card>
);

/**
 * POC展示配置页面组件
 */
const PocDisplaySettings: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<PocDisplayConfig>({
        assistTradingEnabled: false,
        autoTradingEnabled: false,
        patternEnabled: false,
    });

    useEffect(() => {
        loadConfig();
    }, []);

    /**
     * 加载配置
     */
    const loadConfig = async () => {
        try {
            setLoading(true);
            const response = await systemConfigService.getPocDisplayConfig();
            const apiResponse = response.data;
            if ('0' === apiResponse.code) {
                setConfig(apiResponse.data);
            }
        } catch (error: any) {
            console.error('加载配置失败:', error);
            message.error('加载配置失败: ' + (error.message || '未知错误'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * 保存配置
     */
    const saveConfig = async (newConfig: PocDisplayConfig) => {
        try {
            setSaving(true);
            await systemConfigService.updatePocDisplayConfig(newConfig);
            message.success('配置已保存');
        } catch (error: any) {
            console.error('保存配置失败:', error);
            message.error('保存配置失败: ' + (error.message || '未知错误'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * 开关变化处理（自动保存）
     */
    const handleSwitchChange = (key: keyof PocDisplayConfig, checked: boolean) => {
        const newConfig = {...config, [key]: checked};
        setConfig(newConfig);
        saveConfig(newConfig);
    };

    return (
        <div style={{padding: '24px', maxWidth: '800px'}}>
            {/* 页面标题 */}
            <div style={{marginBottom: '24px'}}>
                <Title level={3} style={{margin: 0}}>POC展示配置</Title>
            </div>

            {/* 说明文字 */}
            <div style={{marginBottom: '24px'}}>
                <Text type="secondary">
                    分别控制官网三个POC模块的展示状态。当所有POC均禁用时，官网导航栏的POC菜单将整体隐藏。
                </Text>
            </div>

            {/* 配置表单 */}
            <PocSwitchItem
                label="辅助交易 POC"
                description="控制辅助交易POC页面的展示"
                checked={config.assistTradingEnabled}
                onChange={(checked) => handleSwitchChange('assistTradingEnabled', checked)}
                disabled={loading}
                saving={saving}
            />

            <PocSwitchItem
                label="自动交易 POC"
                description="控制自动交易POC页面的展示"
                checked={config.autoTradingEnabled}
                onChange={(checked) => handleSwitchChange('autoTradingEnabled', checked)}
                disabled={loading}
                saving={saving}
            />

            <PocSwitchItem
                label="形态识别 POC"
                description="控制形态识别POC页面的展示"
                checked={config.patternEnabled}
                onChange={(checked) => handleSwitchChange('patternEnabled', checked)}
                disabled={loading}
                saving={saving}
            />
        </div>
    );
};

export default PocDisplaySettings;
